import React, {
    Children,
    memo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import cn from 'classnames';
import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

import type { TSmeCarousel } from './models';
import { useArrows } from './hooks/use-arrows';

export const SmeCarousel = memo(
    ({
        Arrow,
        Dots,
        FullHeightArrow,
        applyFixForCenteringCards,
        arrowsView = 'arrowInCircle',
        autoplay: initAutoplay = true,
        children,
        className,
        constantWidth = false,
        dataTestId,
        delay = 4,
        fullMode = false,
        gapBetweenSlides,
        inViewTSlides = 1,
        invertAlign = false,
        isMobile,
        loop = false,
        noPaddings = false,
        sameSlideHeight,
        scrollToIndex,
        showArrows = true,
        showDots = true,
        slidesToScroll = 1,
        styles,
        watchDrag = true,
    }: TSmeCarousel) => {
        const slidesContainerRef = useRef<HTMLDivElement | null>(null);
        const [slideMinHeight, setSlideMinHeight] = useState<number | undefined>(undefined);

        const [autoplay, setAutoplay] = useState(initAutoplay);

        const options: EmblaOptionsType = useMemo(
            () => ({
                loop,
                speed: 10,
                align: applyFixForCenteringCards ? 'center' : 'start',
                skipSnaps: true,
                startIndex: 0,
                slidesToScroll,
                watchDrag,
            }),
            [loop, slidesToScroll, watchDrag, applyFixForCenteringCards]
        );

        const [emblaRef, emblaApi] = useEmblaCarousel(options, [
            Autoplay({
                delay: delay * 1000,
                playOnInit: autoplay,
            }),
        ]);

        const { arrows, setNextBtnDisabled, setPrevBtnDisabled } = useArrows({
            Arrow,
            FullHeightArrow,
            setAutoplay,
            arrowsView,
            emblaApi,
            fullMode,
        });

        const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
        const [selectedIndex, setSelectedIndex] = useState(0);

        const dotClick = useCallback(
            (index: number) => {
                if (emblaApi) {
                    setAutoplay(false);
                    emblaApi.scrollTo(index);
                }
            },
            [emblaApi]
        );

        const updateDots = useCallback((api: EmblaCarouselType) => {
            setScrollSnaps(api.scrollSnapList());

            // выполняется только на монтирование
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const onSelect = useCallback((api: EmblaCarouselType) => {
            setSelectedIndex(api.selectedScrollSnap());
            setPrevBtnDisabled(!api.canScrollPrev());
            setNextBtnDisabled(!api.canScrollNext());

            // выполняется только на монтирование
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        useLayoutEffect(() => {
            if (sameSlideHeight) {
                setSlideMinHeight(slidesContainerRef.current?.offsetHeight);
            }
        }, [sameSlideHeight]);

        useEffect(() => {
            if (!emblaApi) return;

            updateDots(emblaApi);
            onSelect(emblaApi);
            emblaApi.on('reInit', onSelect);
            emblaApi.on('select', onSelect);
            emblaApi.on('pointerUp', () => setAutoplay(false));
        }, [emblaApi, updateDots, onSelect]);

        useEffect(() => {
            if (!emblaApi) return;
            updateDots(emblaApi);
            onSelect(emblaApi);
        }, [emblaApi, slidesToScroll, inViewTSlides, updateDots, onSelect]);

        useEffect(() => setAutoplay(initAutoplay), [initAutoplay]);

        useEffect(() => {
            if (typeof scrollToIndex === 'number') {
                emblaApi?.scrollTo(scrollToIndex);
            }
        }, [scrollToIndex, emblaApi]);

        const slides = useMemo(
            () =>
                Children.map(children, (banner, index) => {
                    const slideStyle = {
                        ...(constantWidth
                            ? { flexBasis: 'auto' }
                            : { flexBasis: `${(isMobile ? 97.5 : 100) / inViewTSlides}%` }),
                        ...(gapBetweenSlides !== undefined && {
                            paddingLeft:
                                gapBetweenSlides === 0 && index === 0
                                    ? undefined
                                    : `${gapBetweenSlides}px`,
                        }),
                        ...(sameSlideHeight && slideMinHeight && { height: slideMinHeight }),
                    };

                    return (
                        <div
                            key={index}
                            className={cn(styles.slide, {
                                [styles.slideSameHeight]: sameSlideHeight,
                            })}
                            style={slideStyle}
                        >
                            {banner}
                        </div>
                    );
                }),
            [
                children,
                constantWidth,
                gapBetweenSlides,
                inViewTSlides,
                isMobile,
                styles.slide,
                slideMinHeight,
                sameSlideHeight,
                styles.slideSameHeight,
            ]
        );

        return (
            <div className={cn(styles.root, className)} data-test-id={dataTestId}>
                <div className={styles.slider}>
                    <div
                        ref={emblaRef}
                        className={cn(styles.viewport, {
                            [styles.trim]: !fullMode,
                            [styles.viewportNoPaddings]: noPaddings,
                        })}
                    >
                        <div
                            ref={slidesContainerRef}
                            className={cn(styles.container, {
                                [styles.containerLegacyMargin]: !applyFixForCenteringCards,
                                [styles.alignItemsFlexStart]: invertAlign,
                                [styles.alignItemsFlexEnd]: !invertAlign,
                            })}
                            style={{
                                ...(gapBetweenSlides && {
                                    marginLeft: `-${gapBetweenSlides}px`,
                                }),
                            }}
                        >
                            {slides}
                        </div>
                    </div>

                    {showArrows && !isMobile && arrows}
                </div>

                {showDots && (
                    <Dots
                        autoplay={autoplay}
                        delay={delay}
                        onChange={dotClick}
                        selectedIndex={selectedIndex}
                        total={scrollSnaps.length}
                    />
                )}
            </div>
        );
    }
);
